import { useEffect, useState } from "react";
import { Loader2, Printer, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DEFAULT_LABEL, Label, LabelSheet, labelPrice } from "@/components/stock/LabelSheet";
import { LabelConfig } from "@/components/stock/LabelConfig";
import { useAuth } from "@/context/AuthContext";
import { api, brl, canDo, errMsg } from "@/lib/api";

const COUNTED = ["un", "cx", "pct", "dz"];
const MAX = 1000;
const stockCopies = (p) => (p.stock == null || !COUNTED.includes(p.unit || "un") ? 1 : Math.max(0, Math.ceil(p.stock)));
const validCfg = (c) => c.width >= 15 && c.width <= 150 && c.height >= 10 && c.height <= 150;

function CopiesMode({ mode, onMode }) {
  const btn = (k, l) => <button type="button" onClick={() => onMode(k)} data-testid={`labels-copies-mode-${k}`} className={`rounded-md px-3 py-1.5 text-sm transition-colors ${mode === k ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{l}</button>;
  return <div className="flex flex-wrap items-center gap-2 text-sm">Cópias:<div className="flex gap-1 rounded-lg border p-1">{btn("one", "1 por produto")}{btn("stock", "Quantidade em estoque")}</div></div>;
}

function LabelRow({ r, idx, onChange, showExpiry }) {
  return (
    <div className="flex flex-wrap items-center gap-3 px-3 py-2" data-testid={`label-row-${idx}`}>
      <div className="min-w-0 flex-1 basis-40"><p className="truncate text-sm font-medium">{r.name}</p>
        <p className="font-mono text-xs text-muted-foreground">{brl(labelPrice(r))} · {r.barcode || "sem código de barras"}</p></div>
      {showExpiry && <Input type="date" value={r.expiry || ""} onChange={(e) => onChange({ expiry: e.target.value })} className="w-40" title="Validade" data-testid={`label-expiry-${idx}`} />}
      <Input type="number" min="0" step="1" value={r.copies} onChange={(e) => onChange({ copies: e.target.value })} className="w-20" title="Cópias" data-testid={`label-copies-${idx}`} />
    </div>
  );
}

function useLabelRows(lines) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const qty = {};
    const exp = {};
    lines.forEach((l) => { qty[l.item_id] = (qty[l.item_id] || 0) + Number(l.qty || 0); if (l.expiry) exp[l.item_id] = l.expiry; });
    api.post("/products/labels", { product_ids: Object.keys(qty) })
      .then((r) => setRows(r.data.map((p) => ({ ...p, expiry: exp[p.id] || p.expiry_date || "", copies: COUNTED.includes(p.unit || "un") ? Math.ceil(qty[p.id]) : 1 }))))
      .catch((e) => { toast.error(errMsg(e)); setRows([]); });
  }, [lines]);
  return [rows, setRows];
}

export function LabelsDialog({ title, lines, onClose, allowStock = false }) {
  const { session, setSession } = useAuth();
  const canGen = canDo(session.user, "edit_products");
  const [rows, setRows] = useLabelRows(lines);
  const [cfg, setCfg] = useState(session.tenant.settings.label_config || DEFAULT_LABEL);
  const [gen, setGen] = useState(canGen);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(null);
  const [mode, setMode] = useState("one");
  const store = session.tenant.nome_fantasia;
  const onMode = (m) => { setMode(m); setRows((rs) => rs.map((r) => ({ ...r, copies: m === "stock" ? stockCopies(r) : 1 }))); };
  useEffect(() => {
    if (!printing) return undefined;
    const t = setTimeout(() => { window.print(); setPrinting(null); }, 400);
    return () => clearTimeout(t);
  }, [printing]);
  const total = (rows || []).reduce((a, r) => a + Math.max(0, Math.floor(Number(r.copies) || 0)), 0);
  const saveDefault = async () => {
    if (!validCfg(cfg)) return toast.error("Use largura entre 15 e 150 mm e altura entre 10 e 150 mm");
    setSaving(true);
    try {
      const { data } = await api.put("/settings/labels", cfg);
      setSession((s) => ({ ...s, tenant: { ...s.tenant, settings: { ...s.tenant.settings, label_config: data.label_config } } }));
      toast.success("Etiqueta salva como padrão da loja");
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };
  const print = async () => {
    if (!validCfg(cfg)) return toast.error("Use largura entre 15 e 150 mm e altura entre 10 e 150 mm");
    if (!cfg.fields.length) return toast.error("Escolha ao menos uma informação para a etiqueta");
    if (!total) return toast.error("Informe a quantidade de etiquetas");
    if (total > MAX) return toast.error(`Imprima no máximo ${MAX} etiquetas por vez`);
    setBusy(true);
    try {
      const needCode = gen && cfg.fields.includes("barcode") && rows.some((r) => !r.barcode && Number(r.copies) > 0);
      const fresh = needCode ? (await api.post("/products/labels", { product_ids: rows.map((r) => r.id), generate: true })).data : rows;
      const code = Object.fromEntries(fresh.map((p) => [p.id, p.barcode]));
      const merged = rows.map((r) => ({ ...r, barcode: code[r.id] || r.barcode }));
      setRows(merged);
      setPrinting(merged.flatMap((r) => Array.from({ length: Math.max(0, Math.floor(Number(r.copies) || 0)) }, () => r)));
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const patch = (i) => (p) => setRows((rs) => rs.map((x, k) => (k === i ? { ...x, ...p } : x)));
  const missing = (rows || []).filter((r) => !r.barcode).length;
  const ok = validCfg(cfg);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="labels-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Tag className="h-5 w-5 text-primary" />{title}</DialogTitle></DialogHeader>
        {!rows ? <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : !rows.length ? <p className="py-6 text-center text-sm text-muted-foreground">Nenhum produto de venda para etiquetar.</p> : (
          <div className="space-y-4">
            <LabelConfig cfg={cfg} setCfg={setCfg} onSave={saveDefault} saving={saving} />
            <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-center">
              <div className="overflow-hidden rounded-lg border bg-white p-2" style={{ width: "fit-content", maxWidth: "100%" }} data-testid="label-preview">
                {ok && <div style={{ zoom: Math.min(1.5, 230 / (cfg.width * 3.78)) }}><Label p={rows[0]} cfg={cfg} store={store} /></div>}
              </div>
              <p className="text-sm text-muted-foreground" data-testid="label-size-hint">Etiqueta {cfg.width} x {cfg.height} mm. Na janela de impressão, escolha a impressora de etiquetas e o papel {cfg.width} x {cfg.height} mm.</p>
            </div>
            {allowStock && <CopiesMode mode={mode} onMode={onMode} />}
            {missing > 0 && cfg.fields.includes("barcode") && (
              <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm">
                <span>Gerar código de barras da loja para {missing} produto(s) sem código<span className="block text-xs text-muted-foreground">{canGen ? "Desligado: a etiqueta sai sem código de barras" : "Só quem pode editar produtos gera códigos"}</span></span>
                <Switch checked={gen} disabled={!canGen} onCheckedChange={setGen} data-testid="labels-generate-switch" />
              </label>
            )}
            <div className="divide-y rounded-lg border">{rows.map((r, i) => <LabelRow key={r.id} r={r} idx={i} onChange={patch(i)} showExpiry={cfg.fields.includes("expiry")} />)}</div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="labels-close-btn">Fechar</Button>
          <Button onClick={print} disabled={busy || !rows?.length} data-testid="labels-print-btn">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Printer className="mr-1 h-4 w-4" />}Imprimir {total} etiqueta(s)</Button>
        </DialogFooter>
        {printing && <LabelSheet labels={printing} cfg={cfg} store={store} />}
      </DialogContent>
    </Dialog>
  );
}
